import React from 'react';
import { Button } from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';
import { filterNullish } from '@modernhealth/filter-nullish';
import styled from '@emotion/styled';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';

import { toastNotificationsService } from 'src/config/rootService';
import { GetMyProvidersDocument } from 'src/modules/providers/documents/getMyProvidersQuery.generated';
import { PathwayDocument } from 'src/modules/pathways/documents/pathway.generated';
import { useStartMemberPathwayMutation } from 'src/modules/pathways/documents/startMemberPathway.generated';
import { PathwayProviderModalRouteToolkit } from 'src/modules/pathways/pages/PathwayProviderModalPage';
import { ProviderProfileCard } from 'src/modules/pathways/components/ProviderProfileCard';
import { PathwayGoals } from 'src/modules/pathways/components/PathwayGoals';
import { PathwayCreatedHeader } from 'src/modules/pathways/components/PathwayCreatedHeader';
import { PageContentContainer } from 'src/modules/pathways/components/PageLayout';
import {
    ImageBottomLeft,
    ImageBottomRight,
} from 'src/modules/pathways/components/PageIllustrations';

import type { PathwayDetailProps } from '../PathwayPage';

export const PathwayCreated = ({ pathway, memberPathway }: PathwayDetailProps) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const navigateToPathwayProviderModal = PathwayProviderModalRouteToolkit.useNavigate();
    const [startMemberPathway] = useStartMemberPathwayMutation({
        refetchQueries: [
            { query: PathwayDocument, variables: { uuid: pathway.uuid } },
            { query: GetMyProvidersDocument },
        ],
        awaitRefetchQueries: true,
        variables: {
            input: {
                pathwayUuid: pathway.uuid,
            },
        },
    });

    const pathwayTopics = React.useMemo(
        () => filterNullish(memberPathway.pathwayTopics),
        [memberPathway.pathwayTopics],
    );

    return (
        <PageContentContainer width={552}>
            <CenteredContainer>
                <PathwayCreatedHeader
                    title={pathway.title}
                    sessionCount={pathway.sessionCount}
                    duration={memberPathway.duration}
                    provider={memberPathway.pathwayProvider?.provider}
                />
                <ProviderProfileCard
                    provider={memberPathway.pathwayProvider?.provider}
                    onClick={() => {
                        analytics.trackEvent(EventLexicon.clickViewProviderProfile());
                        navigateToPathwayProviderModal.navigate();
                    }}
                />
                <PathwayGoals pathwayTopics={pathwayTopics} />
                <Button
                    fluid
                    variant="primary"
                    size="large"
                    onClick={async () => {
                        analytics.trackEvent(
                            EventLexicon.clickStartPathway({
                                providerUuid: memberPathway.pathwayProvider.provider.uuid,
                                pathwayUuid: pathway.uuid,
                            }),
                        );
                        try {
                            const { data } = await startMemberPathway();
                            if (!data?.startMemberPathway?.ok) {
                                throw new Error('Unable to start pathway');
                            }
                        } catch (error) {
                            toastNotificationsService.renderErrorToast(
                                'pathways:onboarding.start.error',
                            );
                        }
                    }}
                    icon="arrow-right"
                    iconAlign="end"
                >
                    {t('pathways:action.start_pathway')}
                </Button>
            </CenteredContainer>
            <ImageBottomLeft />
            <ImageBottomRight />
        </PageContentContainer>
    );
};

const CenteredContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, #FFFFFF 82.29%)',
});
